import { ApiError, LIMITS, api, type MessageReactions, type Person } from '@sd/contracts';
import { ClientModule } from './context';
import { applyReactionState } from './merge';
import type { ClientState } from './state';
import { withPendingReactions } from './timeline';

function withoutPending(pending: ClientState['pendingReactions'], messageId: string, emoji: string): ClientState['pendingReactions'] {
  const { [emoji]: _removed, ...rest } = pending[messageId] ?? {};
  const { [messageId]: _message, ...others } = pending;
  return Object.keys(rest).length ? { ...others, [messageId]: rest } : others;
}

/**
 * The viewer's reactions. A click shows at once and goes over REST like an edit,
 * never queued for later: a refusal or a dropped connection takes the guess back
 * and rejects, so the screen can say why. Requests for the same message and emoji
 * run one after another, so the last click is what the server keeps.
 */
export class Reactions extends ClientModule {
  private readonly latest = new Map<string, Promise<void>>();

  toggle(conversationId: string, messageId: string, emoji: string): Promise<void> {
    const message = this.state.timelines[conversationId]?.messages.find((m) => m.id === messageId);
    if (!message || message.deletedAt) return Promise.reject(new ApiError('NOT_FOUND', 'message not loaded'));
    const shown = withPendingReactions(message.reactions ?? [], this.state.pendingReactions[messageId], this.state.me?.id ?? '');
    const current = shown.find((r) => r.emoji === emoji);
    const on = !current?.me;
    if (on && !current && shown.length >= LIMITS.maxReactionsPerMessage) return Promise.reject(new ApiError('TOO_MANY_REACTIONS', 'too many different emoji'));
    this.set((s) => ({ pendingReactions: { ...s.pendingReactions, [messageId]: { ...s.pendingReactions[messageId], [emoji]: on } } }));

    const key = `${messageId} ${emoji}`;
    const request: Promise<void> = (this.latest.get(key) ?? Promise.resolve())
      .catch(() => {})
      .then(async () => {
        const endpoint = on ? api.messages.addReaction : api.messages.removeReaction;
        let answer: MessageReactions | null = null;
        try {
          answer = await this.api.call(endpoint, { params: { conversationId, messageId, emoji } });
        } finally {
          this.settle(conversationId, key, request, messageId, emoji, answer);
        }
      });
    this.latest.set(key, request);
    return request;
  }

  /** The server's answer replaces the guess; only the newest request for the key takes the guess away. */
  private settle(conversationId: string, key: string, request: Promise<void>, messageId: string, emoji: string, answer: MessageReactions | null) {
    const newest = this.latest.get(key) === request;
    if (newest) this.latest.delete(key);
    this.set((s) => {
      const timeline = s.timelines[conversationId];
      return {
        ...(answer && timeline ? { timelines: { ...s.timelines, [conversationId]: { ...timeline, messages: applyReactionState(timeline.messages, answer) } } } : {}),
        ...(newest ? { pendingReactions: withoutPending(s.pendingReactions, messageId, emoji) } : {}),
      };
    });
  }

  /** Everyone who reacted, per emoji (the summary names only the first few). */
  async list(conversationId: string, messageId: string): Promise<Array<{ emoji: string; people: Person[] }>> {
    const res = await this.api.call(api.messages.reactions, { params: { conversationId, messageId } });
    const people = this.state.people;
    return res.reactions.map((r) => ({ emoji: r.emoji, people: r.users.map((u) => people[u.userId]).filter((p): p is Person => Boolean(p)) }));
  }
}
